// LLB tutoring orders: create + list. Order is paid via PayFast; on ITN
// success the user is enrolled in each module (course_type='llb_tutoring').

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import {
  LLB_MODULES,
  getLlbModule,
  priceLlbTutoring,
} from "@/lib/llb-modules";

const SEMESTERS = ["2026-S1", "2026-S2"] as const;

const createInput = z.object({
  semester: z.enum(SEMESTERS),
  moduleCodes: z.array(z.string().trim().min(2).max(20)).min(1).max(20),
});

function randomSuffix(n = 6) {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < n; i++) {
    s += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return s;
}

export const createLlbTutoringOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => createInput.parse(i))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;

    // Enforce: LLB services are invitation-only (we must have emailed them
    // LLB content, or they already have LLB history).
    {
      const { checkLlbAccess, LLB_ACCESS_DENIED_MESSAGE } = await import(
        "@/lib/api/llb-access.server"
      );
      const access = await checkLlbAccess(context.userId);
      if (!access.allowed) throw new Error(LLB_ACCESS_DENIED_MESSAGE);
    }

    // Enforce: only registered UNISA students may enrol for LLB tutoring.
    const { data: prof, error: pErr } = await sb
      .from("profiles")
      .select("unisa_student_number")
      .eq("id", context.userId)
      .maybeSingle();
    if (pErr) throw new Error(pErr.message);
    const unisa = (prof as any)?.unisa_student_number?.trim();
    if (!unisa || !/^\d{7,9}$/.test(unisa)) {
      throw new Error(
        "Add your UNISA student number before paying for LLB tutoring (7-9 digits).",
      );
    }

    // Dedupe + validate codes against the catalog
    const codes = Array.from(new Set(data.moduleCodes.map((c) => c.toUpperCase())));
    const unknown = codes.filter((c) => !getLlbModule(c));
    if (unknown.length) {
      throw new Error(`Unknown module code(s): ${unknown.join(", ")}`);
    }

    const pricing = priceLlbTutoring(codes.length);

    const today = new Date();
    const dateStr =
      today.getUTCFullYear().toString() +
      String(today.getUTCMonth() + 1).padStart(2, "0") +
      String(today.getUTCDate()).padStart(2, "0");
    const reference = `NDLCS-LLBTUT-${dateStr}-${context.userId.replace(/-/g, "").slice(0, 6).toUpperCase()}-${randomSuffix(6)}`;

    const { error } = await sb.from("llb_tutoring_orders" as any).insert({
      user_id: context.userId,
      reference,
      semester: data.semester,
      module_codes: codes,
      module_count: codes.length,
      subtotal_zar: pricing.subtotal,
      discount_zar: pricing.discount,
      amount_zar: pricing.total,
      status: "pending",
    });
    if (error) throw new Error(error.message);

    return {
      reference,
      semester: data.semester,
      moduleCodes: codes,
      ...pricing,
    };
  });

// Save / update the learner's UNISA student number on their profile.
const unisaInput = z.object({
  unisaNumber: z.string().trim().regex(/^\d{7,9}$/, "UNISA student number must be 7-9 digits"),
});

export const setMyUnisaStudentNumber = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => unisaInput.parse(i))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("profiles")
      .update({ unisa_student_number: data.unisaNumber })
      .eq("id", context.userId);
    if (error) throw new Error(error.message);
    return { unisaNumber: data.unisaNumber };
  });

export const getMyUnisaStudentNumber = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("profiles")
      .select("unisa_student_number, full_name, email")
      .eq("id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return {
      unisaNumber: (data as any)?.unisa_student_number ?? null,
      fullName: (data as any)?.full_name ?? null,
      email: (data as any)?.email ?? null,
    };
  });

export const listMyLlbTutoringOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("llb_tutoring_orders" as any)
      .select(
        "id, reference, semester, module_codes, module_count, subtotal_zar, discount_zar, amount_zar, status, paid_at, created_at",
      )
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { orders: data ?? [] };
  });

export const getLlbTutoringCatalog = createServerFn({ method: "GET" }).handler(
  async () => ({ modules: LLB_MODULES }),
);

// -----------------------------------------------------------------------------
// Daily tips: for every module the student is enrolled in (course_type='llb_tutoring'),
// surface today's AI-generated study tip. If today's tip is missing, generate
// it on demand via the Lovable AI gateway and persist it.
// -----------------------------------------------------------------------------

async function generateTipText(moduleCode: string, moduleTitle: string, notes: string) {
  const key = process.env.LOVABLE_API_KEY;
  if (!key) {
    // Fallback: rotate over the pre-loaded notes.
    return `Today's focus for ${moduleCode}: ${notes}`;
  }
  try {
    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Lovable-API-Key": key },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          {
            role: "system",
            content:
              "You are a UNISA LLB tutor. Produce ONE short daily study tip (max 2 sentences, ~50 words) for a South African law student. Be specific, exam-focused, and reference the SA legal context.",
          },
          {
            role: "user",
            content: `Module ${moduleCode} — ${moduleTitle}.\nContext for the tutor: ${notes}\n\nReturn only the tip text.`,
          },
        ],
      }),
    });
    if (!res.ok) throw new Error(`AI ${res.status}`);
    const data = (await res.json()) as { choices?: { message?: { content?: string } }[] };
    const text = (data.choices?.[0]?.message?.content ?? "").trim();
    return text || `Today's focus for ${moduleCode}: ${notes}`;
  } catch {
    return `Today's focus for ${moduleCode}: ${notes}`;
  }
}

export const getMyLlbDailyTips = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = context.supabase;
    const userId = context.userId;

    // Find enrolled tutoring modules.
    const { data: enrolments, error: enrErr } = await sb
      .from("student_module_enrollments")
      .select("module_code, module_name")
      .eq("user_id", userId)
      .eq("course_type", "llb_tutoring")
      .eq("status", "enrolled");
    if (enrErr) throw new Error(enrErr.message);

    const today = new Date().toISOString().slice(0, 10);
    const codes = (enrolments ?? []).map((e: any) => e.module_code);
    if (codes.length === 0) return { tips: [] as any[], today };

    // Existing tips for today.
    const { data: existing } = await sb
      .from("llb_daily_tips" as any)
      .select("module_code, module_title, tip, day, last_email_status, last_email_error, last_emailed_at")

      .eq("user_id", userId)
      .eq("day", today)
      .in("module_code", codes);
    const haveByCode = new Map<string, any>(
      (existing ?? []).map((r: any) => [r.module_code, r]),
    );

    // Generate missing tips (admin client) so the row insert bypasses learner-RLS.
    const missing = (enrolments ?? []).filter((e: any) => !haveByCode.has(e.module_code));
    if (missing.length) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      for (const e of missing) {
        const mod = getLlbModule(e.module_code);
        const moduleTitle = e.module_name || mod?.title || e.module_code;
        const tip = await generateTipText(
          e.module_code,
          moduleTitle,
          mod?.notes ?? "",
        );
        const { data: inserted } = await supabaseAdmin
          .from("llb_daily_tips" as any)
          .upsert(
            {
              user_id: userId,
              module_code: e.module_code,
              module_title: moduleTitle,
              day: today,
              tip,
            } as any,
            { onConflict: "user_id,module_code,day" } as any,
          )
          .select("module_code, module_title, tip, day, last_email_status, last_email_error, last_emailed_at")
          .maybeSingle();
        if (inserted) haveByCode.set(e.module_code, inserted);
      }
    }

    return {
      today,
      tips: Array.from(haveByCode.values()),
    };
  });

// -----------------------------------------------------------------------------
// Tips history + regenerate today's tip
// -----------------------------------------------------------------------------

export const listMyLlbTipsHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("llb_daily_tips" as any)
      .select("module_code, module_title, tip, day, created_at, last_email_status, last_email_error, last_emailed_at")
      .eq("user_id", context.userId)
      .order("day", { ascending: false })
      .order("module_code", { ascending: true })
      .limit(200);
    if (error) throw new Error(error.message);
    return { tips: data ?? [] };
  });


export const regenerateMyLlbTipToday = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ moduleCode: z.string().min(2).max(20) }).parse(i))
  .handler(async ({ data, context }) => {
    const today = new Date().toISOString().slice(0, 10);
    const code = data.moduleCode.toUpperCase();

    // Verify enrolment so users can only regenerate tips for their modules.
    const { data: enrol, error: enrErr } = await context.supabase
      .from("student_module_enrollments")
      .select("module_code, module_name")
      .eq("user_id", context.userId)
      .eq("course_type", "llb_tutoring")
      .eq("status", "enrolled")
      .eq("module_code", code)
      .maybeSingle();
    if (enrErr) throw new Error(enrErr.message);
    if (!enrol) throw new Error("You are not enrolled in this module.");

    const mod = getLlbModule(code);
    const moduleTitle = (enrol as any).module_name || mod?.title || code;
    const tip = await generateTipText(code, moduleTitle, mod?.notes ?? "");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Look up the user's email and attempt to enqueue a tip email.
    let emailStatus: "queued" | "skipped" | "failed" = "skipped";
    let emailError: string | null = null;
    try {
      const { data: prof } = await supabaseAdmin
        .from("profiles")
        .select("email, full_name")
        .eq("id", context.userId)
        .maybeSingle();
      const to = (prof as any)?.email as string | undefined;
      if (to) {
        const { error: qErr } = await supabaseAdmin.rpc("enqueue_email" as any, {
          queue_name: "transactional",
          payload: {
            templateName: "llb-daily-tip",
            recipientEmail: to,
            idempotencyKey: `llb-tip-${context.userId}-${code}-${today}`,
            templateData: {
              name: (prof as any)?.full_name ?? null,
              moduleCode: code,
              moduleTitle,
              tip,
              day: today,
            },
          },
        });
        if (qErr) {
          emailStatus = "failed";
          emailError = qErr.message;
        } else {
          emailStatus = "queued";
        }
      }
    } catch (e: any) {
      emailStatus = "failed";
      emailError = e?.message ?? String(e);
    }

    const { data: row, error } = await supabaseAdmin
      .from("llb_daily_tips" as any)
      .upsert(
        {
          user_id: context.userId,
          module_code: code,
          module_title: moduleTitle,
          day: today,
          tip,
          last_email_status: emailStatus,
          last_email_error: emailError,
          last_emailed_at: emailStatus === "queued" ? new Date().toISOString() : null,
        } as any,
        { onConflict: "user_id,module_code,day" } as any,
      )
      .select("module_code, module_title, tip, day, created_at, last_email_status, last_email_error, last_emailed_at")
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { tip: row, emailStatus, emailError };
  });

// -----------------------------------------------------------------------------
// Module download tracker (notes / templates)
// -----------------------------------------------------------------------------

export const recordLlbModuleDownload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        moduleCode: z.string().min(2).max(20),
        asset: z.string().min(1).max(120),
        assetLabel: z.string().max(200).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("llb_module_downloads" as any).insert({
      user_id: context.userId,
      module_code: data.moduleCode.toUpperCase(),
      asset: data.asset,
      asset_label: data.assetLabel ?? null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listMyLlbDownloads = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("llb_module_downloads" as any)
      .select("id, module_code, asset, asset_label, created_at")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return { downloads: data ?? [] };
  });


// -----------------------------------------------------------------------------
// Receipts + access audit: payment events for the user's tutoring references
// plus their tutoring enrolment history.
// -----------------------------------------------------------------------------

export const getMyLlbReceiptsAndAudit = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = context.supabase;
    const userId = context.userId;

    // 1) Orders owned by this user (RLS-safe).
    const { data: orders, error: oErr } = await sb
      .from("llb_tutoring_orders" as any)
      .select(
        "id, reference, semester, module_codes, module_count, subtotal_zar, discount_zar, amount_zar, status, paid_at, created_at",
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (oErr) throw new Error(oErr.message);

    const refs = (orders ?? []).map((o: any) => o.reference).filter(Boolean);

    // 2) Payment events: payment_events is admin-only (RLS). Use admin client
    //    but restrict to the user's own references.
    let events: any[] = [];
    if (refs.length) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: evs } = await supabaseAdmin
        .from("payment_events")
        .select("id, provider, event_type, reference, processed, error, created_at, processed_at")
        .in("reference", refs)
        .order("created_at", { ascending: false })
        .limit(500);
      events = evs ?? [];
    }

    // 3) Module access log = student enrolments for tutoring.
    const { data: enrolments } = await sb
      .from("student_module_enrollments")
      .select("module_code, module_name, status, started_at, completed_at, created_at")
      .eq("user_id", userId)
      .eq("course_type", "llb_tutoring")
      .order("created_at", { ascending: false });

    // 4) Receipt-email job status for those refs.
    let receiptJobs: any[] = [];
    if (refs.length) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: jobs } = await supabaseAdmin
        .from("receipt_jobs")
        .select("reference, status, attempts, last_error, sent_at, created_at")
        .in("reference", refs)
        .order("created_at", { ascending: false });
      receiptJobs = jobs ?? [];
    }

    // 5) Download tracker entries.
    const { data: downloads } = await sb
      .from("llb_module_downloads" as any)
      .select("id, module_code, asset, asset_label, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(500);

    return { orders: orders ?? [], events, enrolments: enrolments ?? [], receiptJobs, downloads: downloads ?? [] };

  });

// -----------------------------------------------------------------------------
// Admin: list LLB tutoring orders grouped by student with PayFast status,
// discount info, and auto-enrolment results.
// -----------------------------------------------------------------------------

export const adminListLlbTutoring = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./_admin-guard");
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: orders, error } = await supabaseAdmin
      .from("llb_tutoring_orders")
      .select(
        "id, user_id, reference, semester, module_codes, module_count, subtotal_zar, discount_zar, amount_zar, status, paid_at, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);

    const userIds = Array.from(new Set((orders ?? []).map((o: any) => o.user_id)));
    const refs = (orders ?? []).map((o: any) => o.reference).filter(Boolean);

    const [{ data: profiles }, { data: events }, { data: enrolments }] = await Promise.all([
      supabaseAdmin
        .from("profiles")
        .select("id, full_name, email, student_number")
        .in("id", userIds.length ? userIds : ["00000000-0000-0000-0000-000000000000"]),
      refs.length
        ? supabaseAdmin
            .from("payment_events")
            .select("reference, event_type, processed, error, created_at")
            .in("reference", refs)
            .order("created_at", { ascending: false })
        : Promise.resolve({ data: [] as any[] }),
      supabaseAdmin
        .from("student_module_enrollments")
        .select("user_id, module_code, module_name, status, created_at")
        .eq("course_type", "llb_tutoring")
        .in("user_id", userIds.length ? userIds : ["00000000-0000-0000-0000-000000000000"]),
    ]);

    const profileById = new Map((profiles ?? []).map((p: any) => [p.id, p]));
    const eventsByRef = new Map<string, any[]>();
    for (const e of events ?? []) {
      const arr = eventsByRef.get(e.reference) ?? [];
      arr.push(e);
      eventsByRef.set(e.reference, arr);
    }
    const enrolByUser = new Map<string, any[]>();
    for (const en of enrolments ?? []) {
      const arr = enrolByUser.get(en.user_id) ?? [];
      arr.push(en);
      enrolByUser.set(en.user_id, arr);
    }

    const rows = (orders ?? []).map((o: any) => ({
      ...o,
      student: profileById.get(o.user_id) ?? null,
      events: eventsByRef.get(o.reference) ?? [],
      enrolments: (enrolByUser.get(o.user_id) ?? []).filter((e: any) =>
        (o.module_codes ?? []).includes(e.module_code),
      ),
    }));

    return { rows };
  });


// -----------------------------------------------------------------------------
// Tailored AI-generated notes: student picks one of their enrolled modules,
// names a topic + focus, and we produce briefed, paywall-grade notes. Saved
// to llb_tailored_notes, rendered to a branded PDF, uploaded to private
// storage, and emailed to the learner.
// -----------------------------------------------------------------------------

const tailoredInput = z.object({
  moduleCode: z.string().trim().min(2).max(20),
  topic: z.string().trim().min(4).max(200),
  focus: z.string().trim().max(800).optional(),
  sourceNotesMd: z.string().trim().max(20000).optional(),
});

async function generateTailoredNotesMd(args: {
  moduleCode: string;
  moduleTitle: string;
  topic: string;
  focus?: string;
  baseNotes: string;
  sourceNotesMd?: string;
}) {
  const key = process.env.LOVABLE_API_KEY;
  const seedNotes = args.sourceNotesMd?.trim()
    ? args.sourceNotesMd.trim()
    : args.baseNotes;
  if (!key) {
    return {
      content: `# ${args.moduleCode} — ${args.topic}\n\n${seedNotes}\n\n(Tailored notes generator is offline; using your supplied / tutor base notes.)`,
      model: "fallback",
    };
  }
  const system = [
    "You are a senior UNISA LLB tutor producing PAID, exam-grade briefed notes.",
    "Audience: South African LLB students. Use SA law and constitutional context.",
    "Quality bar: clearer and more useful than a textbook chapter. Worth paying for.",
    "LENGTH REQUIREMENT (non-negotiable): produce 2,800–4,200 words of substantive content,",
    "  which renders as roughly 5–8 A4 pages once formatted. Minimum 5 pages of actual notes",
    "  (excluding the closing policy). Do NOT pad — depth, examples, cases and statute references",
    "  must justify the length. If a section is thin, expand with worked examples and authority.",
    "POLICY (must appear at the very end of your output, verbatim, as a final '## Important' section):",
    "  We do NOT write examinations, tests, or assignments on behalf of students.",
    "  These notes are study and tutoring material only. Students must produce their own assessed work.",
    "If the user pastes their own source notes, REWRITE and EXPAND them — keep the user's facts,",
    "improve structure, accuracy, citations, and exam-readiness. Never copy verbatim placeholders.",
    "Required structure (use markdown headings exactly, and write substantively under EACH):",
    "# {Module code} — {Topic}",
    "## 1. Plain-English overview (≥250 words)",
    "## 2. Key principles & doctrine (≥400 words, with sub-bullets per principle)",
    "## 3. Leading cases (≥5 cases: name, citation, facts in 2–3 lines, ratio, why it matters)",
    "## 4. Statutory framework (cite Acts and section numbers; explain each in your own words)",
    "## 5. Step-by-step IRAC application (a worked example, ≥350 words)",
    "## 6. Common exam traps & how to avoid them (≥6 traps with fixes)",
    "## 7. Past-paper style problem question + full model answer outline (≥500 words)",
    "## 8. Quick-revision summary (concise bullet list of the 12–20 must-know points)",
    "## Important",
    "Be specific, cite primary authority, no filler, no placeholders, no 'as discussed above' fluff.",
  ].join("\n");
  const user = `Module: ${args.moduleCode} — ${args.moduleTitle}\nTopic: ${args.topic}\n${
    args.focus ? `Focus / questions: ${args.focus}\n` : ""
  }${
    args.sourceNotesMd?.trim()
      ? `Student-supplied source notes to REWRITE and EXPAND (treat as authoritative on facts the student emphasised):\n"""\n${args.sourceNotesMd.trim()}\n"""\n`
      : ""
  }Tutor base notes: ${args.baseNotes}\n\nProduce the FULL 5–8 page briefed notes now. Aim for 3,000+ words.`;

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 90000);
  try {
    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Lovable-API-Key": key },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        max_tokens: 8000,
        messages: [
          { role: "system", content: system },
          { role: "user", content: user },
        ],
      }),
      signal: ctrl.signal,
    });
    if (!res.ok) throw new Error(`AI ${res.status}`);
    const j = (await res.json()) as { choices?: { message?: { content?: string } }[] };
    const text = (j.choices?.[0]?.message?.content ?? "").trim();
    if (!text) throw new Error("empty");
    return { content: text, model: "google/gemini-2.5-flash" };
  } catch (e: any) {
    return {
      content: `# ${args.moduleCode} — ${args.topic}\n\n${seedNotes}\n\nNote: AI generator unavailable (${e?.message ?? "error"}). Fallback uses your supplied / tutor base notes.\n\n## Important\nWe do not write examinations, tests, or assignments on behalf of students. These notes are study and tutoring material only.`,
      model: "fallback",
    };
  } finally {
    clearTimeout(timer);
  }
}


function mdToPdfBlocks(md: string) {
  const blocks: any[] = [];
  const lines = md.split(/\r?\n/);
  let buf: string[] = [];
  const flushP = () => {
    if (buf.length) {
      blocks.push({ kind: "p", text: buf.join(" ").trim() });
      buf = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushP(); blocks.push({ kind: "spacer", size: 3 }); continue; }
    if (line.startsWith("# ")) { flushP(); blocks.push({ kind: "h1", text: line.slice(2).trim() }); continue; }
    if (line.startsWith("## ")) { flushP(); blocks.push({ kind: "h2", text: line.slice(3).trim() }); continue; }
    if (line.startsWith("### ")) { flushP(); blocks.push({ kind: "h2", text: line.slice(4).trim() }); continue; }
    if (/^[-*]\s+/.test(line)) { flushP(); blocks.push({ kind: "li", text: line.replace(/^[-*]\s+/, "") }); continue; }
    if (/^\d+\.\s+/.test(line)) { flushP(); blocks.push({ kind: "li", text: line.replace(/^\d+\.\s+/, "") }); continue; }
    buf.push(line);
  }
  flushP();
  return blocks;
}

const TAILORED_BUCKET = "study-notes-deliveries";

export const requestLlbTailoredNotes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => tailoredInput.parse(i))
  .handler(async ({ data, context }) => {
    {
      const { checkLlbAccess, LLB_ACCESS_DENIED_MESSAGE } = await import(
        "@/lib/api/llb-access.server"
      );
      const access = await checkLlbAccess(context.userId);
      if (!access.allowed) throw new Error(LLB_ACCESS_DENIED_MESSAGE);
    }
    const code = data.moduleCode.toUpperCase();

    // Check module settings (admin-controlled access + notes availability).
    // Settings live behind an admin-only RLS policy; read via service role.
    const { supabaseAdmin: sbAdminForSettings } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: settings } = await sbAdminForSettings
      .from("llb_module_settings" as any)
      .select("access, notes_enabled, allow_free_tailored")
      .eq("module_code", code)
      .maybeSingle();
    const s = (settings as any) ?? { access: "enrolled", notes_enabled: true, allow_free_tailored: false };
    if (!s.notes_enabled) {
      throw new Error("Tailored notes are not available for this module.");
    }

    // Enrolment check — bypassed only when module is marked free AND
    // tailored notes are explicitly opened to free users.
    const { data: enrol } = await context.supabase
      .from("student_module_enrollments")
      .select("module_code, module_name")
      .eq("user_id", context.userId)
      .eq("course_type", "llb_tutoring")
      .eq("status", "enrolled")
      .eq("module_code", code)
      .maybeSingle();

    const freeAccessOk = s.access === "free" && s.allow_free_tailored === true;
    // Admin bypass: admins always have free access to all tutoring content.
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!enrol && !freeAccessOk && !isAdmin) {
      throw new Error(
        "This module is reserved for enrolled students. Pay for the module to unlock tailored notes.",
      );
    }

    const mod = getLlbModule(code);
    const moduleTitle = (enrol as any)?.module_name || mod?.title || code;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row, error: insErr } = await supabaseAdmin
      .from("llb_tailored_notes" as any)
      .insert({
        user_id: context.userId,
        module_code: code,
        module_title: moduleTitle,
        topic: data.topic,
        focus: data.focus ?? null,
        source_notes_md: data.sourceNotesMd ?? null,
        status: "generating",
      })
      .select("*")
      .single();
    if (insErr) throw new Error(insErr.message);

    try {
      const gen = await generateTailoredNotesMd({
        moduleCode: code,
        moduleTitle,
        topic: data.topic,
        focus: data.focus,
        baseNotes: mod?.notes ?? "",
        sourceNotesMd: data.sourceNotesMd,
      });


      const { buildBrandedPdf } = await import("@/lib/pdf-brand.server");
      const origin = process.env.SITE_URL ?? "https://nkadimengdannylcs.com";
      const pdf = await buildBrandedPdf({
        title: `${code} — ${data.topic}`,
        documentLabel: "Tailored LLB Notes",
        reference: (row as any).id,
        blocks: mdToPdfBlocks(gen.content),
        origin,
        watermark: "NDLCS · LLB Tutoring",
      });

      const path = `llb-tailored/${context.userId}/${(row as any).id}.pdf`;
      const { error: upErr } = await supabaseAdmin.storage
        .from(TAILORED_BUCKET)
        .upload(path, pdf, { contentType: "application/pdf", upsert: true });
      if (upErr) throw new Error(`upload: ${upErr.message}`);

      let emailStatus: string | null = null;
      let emailError: string | null = null;
      try {
        const { data: signed } = await supabaseAdmin.storage
          .from(TAILORED_BUCKET)
          .createSignedUrl(path, 60 * 60 * 24 * 7);
        const { data: prof } = await supabaseAdmin
          .from("profiles")
          .select("email, full_name")
          .eq("id", context.userId)
          .maybeSingle();
        const to = (prof as any)?.email as string | undefined;
        if (to && signed?.signedUrl) {
          const previewText = gen.content.slice(0, 600);
          const { error: qErr } = await supabaseAdmin.rpc("enqueue_email" as any, {
            queue_name: "transactional",
            payload: {
              templateName: "llb-tailored-notes",
              recipientEmail: to,
              idempotencyKey: `llb-tailored-${(row as any).id}`,
              templateData: {
                learnerName: (prof as any)?.full_name ?? null,
                moduleCode: code,
                moduleTitle,
                topic: data.topic,
                pdfUrl: signed.signedUrl,
                portalUrl: `${origin}/portal/llb-tutoring`,
                preview: previewText,
              },
            },
          });
          emailStatus = qErr ? "failed" : "queued";
          emailError = qErr?.message ?? null;
        } else {
          emailStatus = "skipped";
        }
      } catch (e: any) {
        emailStatus = "failed";
        emailError = e?.message ?? String(e);
      }

      const { data: updated } = await supabaseAdmin
        .from("llb_tailored_notes" as any)
        .update({
          content_md: gen.content,
          pdf_path: path,
          ai_model: gen.model,
          status: "ready",
          last_email_status: emailStatus,
          last_email_error: emailError,
          last_emailed_at: emailStatus === "queued" ? new Date().toISOString() : null,
        })
        .eq("id", (row as any).id)
        .select("*")
        .single();

      return { note: updated ?? row };
    } catch (e: any) {
      await supabaseAdmin
        .from("llb_tailored_notes" as any)
        .update({ status: "failed", error: e?.message ?? String(e) })
        .eq("id", (row as any).id);
      throw new Error(e?.message ?? "Failed to generate tailored notes");
    }
  });

export const listMyLlbTailoredNotes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("llb_tailored_notes" as any)
      .select("id, module_code, module_title, topic, focus, source_notes_md, status, pdf_path, content_md, ai_model, error, last_email_status, last_email_error, last_emailed_at, created_at, updated_at")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { notes: data ?? [] };
  });

export const getLlbTailoredNotePdfUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ id: z.string().uuid(), action: z.enum(["view", "download"]).optional() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("llb_tailored_notes" as any)
      .select("id, user_id, module_code, topic, pdf_path")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row || (row as any).user_id !== context.userId) throw new Error("Not found");
    const path = (row as any).pdf_path as string | null;
    if (!path) throw new Error("PDF not ready yet");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error: sErr } = await supabaseAdmin.storage
      .from(TAILORED_BUCKET)
      .createSignedUrl(path, 60 * 10);
    if (sErr || !signed?.signedUrl) throw new Error(sErr?.message ?? "Could not sign URL");

    // Log to download history.
    const action = data.action ?? "download";
    await context.supabase.from("llb_module_downloads" as any).insert({
      user_id: context.userId,
      module_code: (row as any).module_code,
      asset: `tailored_${action}`,
      asset_label: `Tailored notes: ${(row as any).topic}`,
    });

    return { url: signed.signedUrl };
  });

// Tailored-notes download/view history (subset of llb_module_downloads).
export const listMyTailoredNotesHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("llb_module_downloads" as any)
      .select("id, module_code, asset, asset_label, created_at")
      .eq("user_id", context.userId)
      .like("asset", "tailored_%")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { history: data ?? [] };
  });

// -----------------------------------------------------------------------------
// Admin: module settings (free vs enrolled-only, notes availability)
// -----------------------------------------------------------------------------

export const listLlbModuleSettings = createServerFn({ method: "GET" }).handler(
  async () => {
    // Public read so the portal can show free/locked badges before sign-in.
    // The table itself is admin-only at the RLS level; read via service role
    // and only return non-sensitive display columns.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("llb_module_settings" as any)
      .select("module_code, access, notes_enabled, allow_free_tailored, updated_at");
    if (error) throw new Error(error.message);
    return { settings: data ?? [] };
  },
);

const settingsInput = z.object({
  moduleCode: z.string().trim().min(2).max(20),
  access: z.enum(["free", "enrolled"]),
  notesEnabled: z.boolean(),
  allowFreeTailored: z.boolean(),
});

export const adminUpsertLlbModuleSetting = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => settingsInput.parse(i))
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./_admin-guard");
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const code = data.moduleCode.toUpperCase();
    if (!getLlbModule(code)) throw new Error("Unknown module code");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("llb_module_settings" as any)
      .upsert(
        {
          module_code: code,
          access: data.access,
          notes_enabled: data.notesEnabled,
          allow_free_tailored: data.allowFreeTailored,
          updated_by: context.userId,
        } as any,
        { onConflict: "module_code" } as any,
      )
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return { setting: row };
  });

// -----------------------------------------------------------------------------
// Tailored notes: retry email (user-side and admin-side)
// -----------------------------------------------------------------------------

async function enqueueTailoredNoteEmail(noteId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: note, error } = await supabaseAdmin
    .from("llb_tailored_notes" as any)
    .select("id, user_id, module_code, module_title, topic, pdf_path, content_md")
    .eq("id", noteId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!note) throw new Error("Tailored note not found");
  const n: any = note;
  if (!n.pdf_path) throw new Error("PDF is not ready yet — cannot resend.");

  const { data: signed, error: sErr } = await supabaseAdmin.storage
    .from(TAILORED_BUCKET)
    .createSignedUrl(n.pdf_path, 60 * 60 * 24 * 7);
  if (sErr || !signed?.signedUrl) throw new Error(sErr?.message ?? "Could not sign URL");

  const { data: prof } = await supabaseAdmin
    .from("profiles")
    .select("email, full_name")
    .eq("id", n.user_id)
    .maybeSingle();
  const to = (prof as any)?.email as string | undefined;
  if (!to) throw new Error("Recipient email missing on profile");

  const origin = process.env.SITE_URL ?? "https://nkadimengdannylcs.com";
  const idem = `llb-tailored-${n.id}-retry-${new Date().toISOString().slice(0, 10)}-${Date.now()}`;
  const { error: qErr } = await supabaseAdmin.rpc("enqueue_email" as any, {
    queue_name: "transactional",
    payload: {
      templateName: "llb-tailored-notes",
      recipientEmail: to,
      idempotencyKey: idem,
      templateData: {
        learnerName: (prof as any)?.full_name ?? null,
        moduleCode: n.module_code,
        moduleTitle: n.module_title,
        topic: n.topic,
        pdfUrl: signed.signedUrl,
        portalUrl: `${origin}/portal/llb-tutoring`,
        preview: (n.content_md ?? "").slice(0, 600),
      },
    },
  });

  const emailStatus = qErr ? "failed" : "queued";
  const emailError = qErr?.message ?? null;
  await supabaseAdmin
    .from("llb_tailored_notes" as any)
    .update({
      last_email_status: emailStatus,
      last_email_error: emailError,
      last_emailed_at: emailStatus === "queued" ? new Date().toISOString() : null,
    })
    .eq("id", n.id);

  if (qErr) throw new Error(qErr.message);
  return { ok: true, emailStatus, recipient: to };
}

export const retryMyTailoredNoteEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    // Ownership check via user-scoped client.
    const { data: row, error } = await context.supabase
      .from("llb_tailored_notes" as any)
      .select("id, user_id")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row || (row as any).user_id !== context.userId) throw new Error("Not found");
    return await enqueueTailoredNoteEmail(data.id);
  });

async function logRetryAudit(opts: {
  adminId: string;
  noteId: string;
  studentUserId?: string | null;
  moduleCode?: string | null;
  recipientEmail?: string | null;
  previousStatus?: string | null;
  newStatus?: string | null;
  messageId?: string | null;
  errorMessage?: string | null;
  metadata?: Record<string, any>;
}) {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("llb_email_retry_audit" as any).insert({
      admin_id: opts.adminId,
      note_id: opts.noteId,
      student_user_id: opts.studentUserId ?? null,
      module_code: opts.moduleCode ?? null,
      recipient_email: opts.recipientEmail ?? null,
      previous_status: opts.previousStatus ?? null,
      new_status: opts.newStatus ?? null,
      message_id: opts.messageId ?? null,
      error_message: opts.errorMessage ?? null,
      metadata: opts.metadata ?? {},
    });
  } catch {
    /* never break the retry on audit failures */
  }
}

async function adminRetryWithAudit(noteId: string, adminId: string, source: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: before } = await supabaseAdmin
    .from("llb_tailored_notes" as any)
    .select("id, user_id, module_code, last_email_status")
    .eq("id", noteId)
    .maybeSingle();
  const prev = (before as any)?.last_email_status ?? null;
  let result: any = null;
  let errMsg: string | null = null;
  let newStatus = "failed";
  let messageId: string | null = null;
  try {
    result = await enqueueTailoredNoteEmail(noteId);
    newStatus = result?.emailStatus ?? "queued";
  } catch (e: any) {
    errMsg = e?.message ?? String(e);
    newStatus = "failed";
  }
  try {
    const { data: log } = await supabaseAdmin
      .from("email_send_log")
      .select("message_id")
      .like("message_id", `llb-tailored-${noteId}-retry-%`)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    messageId = (log as any)?.message_id ?? null;
  } catch { /* ignore */ }
  await logRetryAudit({
    adminId,
    noteId,
    studentUserId: (before as any)?.user_id,
    moduleCode: (before as any)?.module_code,
    recipientEmail: result?.recipient ?? null,
    previousStatus: prev,
    newStatus,
    messageId,
    errorMessage: errMsg,
    metadata: { source },
  });
  if (errMsg) throw new Error(errMsg);
  return result;
}

export const adminRetryTailoredNoteEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./_admin-guard");
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    return await adminRetryWithAudit(data.id, context.userId, "single");
  });

// Safety cap: bulk retry is bounded to BULK_RETRY_HARD_CAP notes per run,
// rate-limited to 1 run per admin per 60s, and skips notes already retried
// in the last 10 minutes (regardless of which admin triggered the retry).
const BULK_RETRY_HARD_CAP = 50;
const BULK_RETRY_COOLDOWN_MS = 60_000;
const BULK_RETRY_DEDUP_WINDOW_MS = 10 * 60_000;

export const adminBulkRetryFailedTailoredNotes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        moduleCode: z.string().trim().max(20).optional(),
        from: z.string().optional(),
        to: z.string().optional(),
        userId: z.string().uuid().optional(),
        limit: z.number().int().min(1).max(BULK_RETRY_HARD_CAP).optional(),
      })
      .partial()
      .parse(i ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./_admin-guard");
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Per-admin cooldown: refuse if this admin already ran a bulk retry recently.
    const cooldownSince = new Date(
      Date.now() - BULK_RETRY_COOLDOWN_MS,
    ).toISOString();
    const { count: recentBulkCount } = await supabaseAdmin
      .from("llb_email_retry_audit" as any)
      .select("id", { count: "exact", head: true })
      .eq("admin_id", context.userId)
      .gte("created_at", cooldownSince)
      .contains("metadata", { source: "bulk" });
    if ((recentBulkCount ?? 0) > 0) {
      throw new Error(
        "Rate limit: please wait 60 seconds between bulk retry runs.",
      );
    }

    const requested = Math.min(data.limit ?? 25, BULK_RETRY_HARD_CAP);

    let q = supabaseAdmin
      .from("llb_tailored_notes" as any)
      .select("id")
      .eq("last_email_status", "failed")
      .order("created_at", { ascending: false })
      .limit(requested);
    if (data.moduleCode) q = q.eq("module_code", data.moduleCode.toUpperCase());
    if (data.userId) q = q.eq("user_id", data.userId);
    if (data.from) q = q.gte("created_at", new Date(data.from).toISOString());
    if (data.to)
      q = q.lte(
        "created_at",
        new Date(new Date(data.to).getTime() + 86400000).toISOString(),
      );
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    const candidateIds = (rows ?? []).map((r: any) => r.id);

    // Deduplicate: skip notes any admin already retried inside the window.
    const dedupSince = new Date(
      Date.now() - BULK_RETRY_DEDUP_WINDOW_MS,
    ).toISOString();
    const { data: recent } = candidateIds.length
      ? await supabaseAdmin
          .from("llb_email_retry_audit" as any)
          .select("note_id")
          .in("note_id", candidateIds)
          .gte("created_at", dedupSince)
      : { data: [] as any[] };
    const recentSet = new Set((recent ?? []).map((r: any) => r.note_id));
    const ids = candidateIds.filter((id) => !recentSet.has(id));
    const skippedDuplicates = candidateIds.length - ids.length;

    let succeeded = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await adminRetryWithAudit(id, context.userId, "bulk");
        succeeded++;
      } catch {
        failed++;
      }
    }
    return {
      attempted: ids.length,
      succeeded,
      failed,
      skippedDuplicates,
      cap: BULK_RETRY_HARD_CAP,
    };
  });

export const adminListLlbRetryAudit = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        search: z.string().trim().max(160).optional(),
        moduleCode: z.string().trim().max(20).optional(),
        from: z.string().optional(),
        to: z.string().optional(),
      })
      .partial()
      .parse(i ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./_admin-guard");
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("llb_email_retry_audit" as any)
      .select("id, admin_id, note_id, student_user_id, module_code, recipient_email, previous_status, new_status, message_id, error_message, metadata, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    if (data.moduleCode) q = q.eq("module_code", data.moduleCode.toUpperCase());
    if (data.from) q = q.gte("created_at", new Date(data.from).toISOString());
    if (data.to) q = q.lte("created_at", new Date(new Date(data.to).getTime() + 86400000).toISOString());
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    const userIds = Array.from(
      new Set(
        (rows ?? []).flatMap((r: any) => [r.admin_id, r.student_user_id].filter(Boolean)),
      ),
    );
    const { data: profiles } = userIds.length
      ? await supabaseAdmin.from("profiles").select("id, full_name, email").in("id", userIds)
      : { data: [] as any[] };
    const pById = new Map((profiles ?? []).map((p: any) => [p.id, p]));
    let mapped = (rows ?? []).map((r: any) => ({
      ...r,
      admin: pById.get(r.admin_id) ?? null,
      student: pById.get(r.student_user_id) ?? null,
    }));
    if (data.search) {
      const s = data.search.toLowerCase();
      mapped = mapped.filter(
        (r: any) =>
          r.recipient_email?.toLowerCase().includes(s) ||
          r.message_id?.toLowerCase().includes(s) ||
          r.module_code?.toLowerCase().includes(s) ||
          r.admin?.full_name?.toLowerCase().includes(s) ||
          r.admin?.email?.toLowerCase().includes(s) ||
          r.student?.full_name?.toLowerCase().includes(s) ||
          r.student?.email?.toLowerCase().includes(s),
      );
    }
    return { rows: mapped };
  });

// -----------------------------------------------------------------------------
// Company assets visible to authenticated members
// -----------------------------------------------------------------------------

// Mission, vision and values — bundled into the ZIP and shown in the portal.
const COMPANY_MISSION = `Nkadimeng Danny Legal & Compliance Support

MISSION
To make affordable, accessible legal support, compliance services and legal
education available to every South African — workers, employers, students
and small businesses — through honest, technology-enabled paralegal and
consulting solutions.

VISION
To be South Africa's most trusted independent legal-support practice —
empowering people with knowledge, protecting their rights at work and in
business, and building a generation of legally literate citizens.

VALUES
- Integrity & honesty in every matter
- Affordability and access for ordinary people
- POPIA-aligned confidentiality
- Professional excellence & lifelong learning
- Service to community and country
`;

// Member is "enrolled" if they are an admin OR have at least one paid
// LLB tutoring order. Admins always see everything.
async function assertMemberOrAdmin(
  supabase: any,
  _supabaseAdmin: any,
  userId: string,
): Promise<{ isAdmin: boolean }> {
  // Company brand assets (MOI, Constitution, Compliance Manual etc.) are
  // CONFIDENTIAL internal corporate records. Access is restricted to admin
  // users only — never expose to customers, even signed-in ones.
  const { data: isAdmin } = await supabase.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });
  if (!isAdmin) throw new Error("Forbidden — admin access required");
  return { isAdmin: true };
}

export const listMyCompanyAssets = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await assertMemberOrAdmin(context.supabase, supabaseAdmin, context.userId);
    const { data, error } = await supabaseAdmin
      .from("company_attachments")
      .select(
        "id, kind, label, sort_order, storage_path, original_filename, mime_type, size_bytes, version, created_at, updated_at",
      )
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    const out: any[] = [];
    for (const r of data ?? []) {
      const { data: signed } = await supabaseAdmin.storage
        .from("company-assets")
        .createSignedUrl((r as any).storage_path, 60 * 60);
      out.push({
        id: (r as any).id,
        kind: (r as any).kind,
        label: (r as any).label,
        filename: (r as any).original_filename,
        mimeType: (r as any).mime_type,
        sizeBytes: (r as any).size_bytes,
        version: (r as any).version ?? "v1.0",
        updatedAt: (r as any).updated_at ?? (r as any).created_at,
        url: signed?.signedUrl ?? null,
      });
    }
    return { assets: out };
  });

// Sends a download confirmation email with direct links to the assets the
// customer just downloaded. Fire-and-forget; never throws to the caller.
async function emailAssetDownloadConfirmation(
  supabaseAdmin: any,
  userId: string,
  payload: {
    downloadType: "single" | "bundle";
    bundleName?: string;
    assets: Array<{
      label: string;
      filename?: string;
      version?: string;
      updatedAt?: string;
      url?: string;
    }>;
  },
) {
  try {
    const { data: userRes } = await supabaseAdmin.auth.admin.getUserById(userId);
    const email = userRes?.user?.email;
    if (!email) return;
    const recipientName =
      (userRes.user.user_metadata?.full_name as string | undefined) ||
      (userRes.user.user_metadata?.name as string | undefined) ||
      email.split("@")[0];
    const { sendSystemEmail } = await import("@/lib/email/system-send.server");
    await sendSystemEmail({
      templateName: "company-assets-download",
      recipientEmail: email,
      idempotencyKey: `company-assets-${userId}-${Date.now()}`,
      templateData: {
        recipientName,
        downloadType: payload.downloadType,
        bundleName: payload.bundleName,
        assets: payload.assets,
        portalUrl: "https://nkadimengdannylcs.com/portal/company-assets",
      },
      metadata: {
        kind: "company_assets_download",
        download_type: payload.downloadType,
      },
    });
  } catch (e) {
    console.error("[emailAssetDownloadConfirmation] failed", e);
  }
}

// Notify on a single-file download (called from the client after it follows
// a signed URL). Safe to call repeatedly; no DB state required.
export const notifyCompanyAssetDownload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ assetId: z.string().uuid() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("company_attachments")
      .select("label, original_filename, version, updated_at, storage_path")
      .eq("id", data.assetId)
      .eq("is_active", true)
      .maybeSingle();
    if (!row) return { ok: false };
    const { data: signed } = await supabaseAdmin.storage
      .from("company-assets")
      .createSignedUrl((row as any).storage_path, 60 * 60);
    await emailAssetDownloadConfirmation(supabaseAdmin, context.userId, {
      downloadType: "single",
      assets: [
        {
          label: (row as any).label,
          filename: (row as any).original_filename,
          version: (row as any).version ?? "v1.0",
          updatedAt: ((row as any).updated_at ?? "").slice(0, 10),
          url: signed?.signedUrl ?? undefined,
        },
      ],
    });
    return { ok: true };
  });

// One-click "Download all assets" — bundles every active company document
// plus a MISSION-VISION-VALUES.txt file into a single ZIP, returned as
// base64 for browser download. Free for every signed-in customer.
export const downloadMyCompanyAssetsZip = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await assertMemberOrAdmin(context.supabase, supabaseAdmin, context.userId);

    const { data, error } = await supabaseAdmin
      .from("company_attachments")
      .select("kind, label, storage_path, original_filename, version, updated_at")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);

    const { BlobWriter, TextReader, Uint8ArrayReader, ZipWriter, configure } =
      await import("@zip.js/zip.js");
    configure({ useWebWorkers: false });
    const blobWriter = new BlobWriter("application/zip");
    const zipWriter = new ZipWriter(blobWriter);

    await zipWriter.add(
      "MISSION-VISION-VALUES.txt",
      new TextReader(COMPANY_MISSION),
    );

    const used = new Set<string>();
    const emailedAssets: Array<{
      label: string;
      filename?: string;
      version?: string;
      updatedAt?: string;
      url?: string;
    }> = [];
    for (const r of data ?? []) {
      const { data: blob } = await supabaseAdmin.storage
        .from("company-assets")
        .download((r as any).storage_path);
      if (!blob) continue;
      const raw = (r as any).original_filename || `${(r as any).kind}.pdf`;
      let safe = raw.replace(/[^a-zA-Z0-9._-]+/g, "-");
      let name = `${(r as any).kind}/${safe}`;
      let n = 1;
      while (used.has(name)) {
        name = `${(r as any).kind}/${n}-${safe}`;
        n++;
      }
      used.add(name);
      const bytes = new Uint8Array(await blob.arrayBuffer());
      await zipWriter.add(name, new Uint8ArrayReader(bytes));
      const { data: signed } = await supabaseAdmin.storage
        .from("company-assets")
        .createSignedUrl((r as any).storage_path, 60 * 60);
      emailedAssets.push({
        label: (r as any).label,
        filename: (r as any).original_filename,
        version: (r as any).version ?? "v1.0",
        updatedAt: ((r as any).updated_at ?? "").slice(0, 10),
        url: signed?.signedUrl ?? undefined,
      });
    }

    const zipBlob = await zipWriter.close();
    const buf = new Uint8Array(await zipBlob.arrayBuffer());
    // base64-encode
    let bin = "";
    for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
    const base64 =
      typeof btoa === "function"
        ? btoa(bin)
        : Buffer.from(buf).toString("base64");
    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `ndl-company-assets-${stamp}.zip`;

    // Fire confirmation email (don't block the download)
    void emailAssetDownloadConfirmation(supabaseAdmin, context.userId, {
      downloadType: "bundle",
      bundleName: filename,
      assets: emailedAssets,
    });

    return {
      filename,
      mimeType: "application/zip",
      base64,
      count: (data ?? []).length,
    };
  });

// -----------------------------------------------------------------------------
// Admin: list ALL tailored notes (with full content) + students
// -----------------------------------------------------------------------------

export const adminListLlbTailoredNotes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        search: z.string().trim().max(120).optional(),
        moduleCode: z.string().trim().max(20).optional(),
        emailStatus: z.enum(["any", "queued", "sent", "failed", "skipped"]).optional(),
        from: z.string().optional(),
        to: z.string().optional(),
      })
      .partial()
      .parse(i ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./_admin-guard");
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let q = supabaseAdmin
      .from("llb_tailored_notes" as any)
      .select(
        "id, user_id, module_code, module_title, topic, focus, source_notes_md, status, pdf_path, content_md, ai_model, error, last_email_status, last_email_error, last_emailed_at, created_at, updated_at",
      )
      .order("created_at", { ascending: false })
      .limit(500);
    if (data.moduleCode) q = q.eq("module_code", data.moduleCode.toUpperCase());
    if (data.emailStatus && data.emailStatus !== "any") q = q.eq("last_email_status", data.emailStatus);
    if (data.from) q = q.gte("created_at", new Date(data.from).toISOString());
    if (data.to) q = q.lte("created_at", new Date(new Date(data.to).getTime() + 86400000).toISOString());

    const { data: notes, error } = await q;
    if (error) throw new Error(error.message);

    const userIds = Array.from(new Set((notes ?? []).map((n: any) => n.user_id)));
    const { data: profiles } = userIds.length
      ? await supabaseAdmin
          .from("profiles")
          .select("id, full_name, email, student_number, unisa_student_number")
          .in("id", userIds)
      : { data: [] as any[] };
    const pById = new Map((profiles ?? []).map((p: any) => [p.id, p]));

    let rows = (notes ?? []).map((n: any) => ({ ...n, student: pById.get(n.user_id) ?? null }));

    if (data.search) {
      const s = data.search.toLowerCase();
      rows = rows.filter(
        (r: any) =>
          r.topic?.toLowerCase().includes(s) ||
          r.module_code?.toLowerCase().includes(s) ||
          r.student?.full_name?.toLowerCase().includes(s) ||
          r.student?.email?.toLowerCase().includes(s) ||
          r.student?.student_number?.toLowerCase().includes(s) ||
          r.student?.unisa_student_number?.toLowerCase().includes(s),
      );
    }
    return { rows };
  });

// -----------------------------------------------------------------------------
// Admin: per-student per-module email queue logs (from email_send_log)
// -----------------------------------------------------------------------------

export const adminListLlbEmailLogs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        search: z.string().trim().max(160).optional(),
        templateName: z.string().trim().max(80).optional(),
        status: z.string().trim().max(40).optional(),
        from: z.string().optional(),
        to: z.string().optional(),
      })
      .partial()
      .parse(i ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./_admin-guard");
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let q = supabaseAdmin
      .from("email_send_log")
      .select("id, message_id, template_name, recipient_email, status, error_message, metadata, created_at")
      .in("template_name", ["llb-tailored-notes", "llb-daily-tip"])
      .order("created_at", { ascending: false })
      .limit(1000);

    if (data.templateName) q = q.eq("template_name", data.templateName);
    if (data.status) q = q.eq("status", data.status);
    if (data.from) q = q.gte("created_at", new Date(data.from).toISOString());
    if (data.to) q = q.lte("created_at", new Date(new Date(data.to).getTime() + 86400000).toISOString());

    const { data: logs, error } = await q;
    if (error) throw new Error(error.message);

    // Deduplicate by message_id (latest row per message_id)
    const latest = new Map<string, any>();
    for (const r of logs ?? []) {
      const key = (r as any).message_id ?? (r as any).id;
      if (!latest.has(key)) latest.set(key, r);
    }
    let rows = Array.from(latest.values());

    if (data.search) {
      const s = data.search.toLowerCase();
      rows = rows.filter(
        (r: any) =>
          r.recipient_email?.toLowerCase().includes(s) ||
          r.message_id?.toLowerCase().includes(s) ||
          r.template_name?.toLowerCase().includes(s) ||
          JSON.stringify(r.metadata ?? {}).toLowerCase().includes(s),
      );
    }
    return { rows };
  });

// -----------------------------------------------------------------------------
// "Access flags" for current user — used by the panel to give admins free
// access to every module without an enrolment row.
// -----------------------------------------------------------------------------

export const getMyLlbAccessFlags = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (error) return { isAdmin: false };
    return { isAdmin: !!data };
  });

